// frontend/src/app/components/onboarding/calibration-diagnosis.tsx
//
// Smoke calibration rounds (#138): for each belief the calibration flagged,
// the sampled answers with the judge's evidence, the anchor text, the
// diagnosis (with the sentences it rests on) and one proposed fix, shown as a
// diff the admin can accept, edit or reject. Also the reasoned acknowledgement
// and the per-round history of the framework's calibrations.

import React, { useMemo, useState } from "react";
import {
  Check, X, Pencil, Loader2, ChevronDown, ChevronRight, ShieldAlert, History,
} from "lucide-react";

import type {
  CalibrationAnchors,
  CalibrationBeliefDiagnosis,
  CalibrationHistory,
  CalibrationProposal,
  CalibrationQuestionChange,
} from "@/api/onboarding-measurement-api";

const SCORE_FIELDS = ["score_2", "score_1", "score_0"] as const;
const SCORE_LABEL: Record<(typeof SCORE_FIELDS)[number], string> = {
  score_2: "2", score_1: "1", score_0: "0",
};

const FLAG_LABEL: Record<string, string> = {
  saturation: "Saturated", stuck: "Stuck", coverage: "No coverage", thin: "One question",
};

const KIND_LABEL: Record<string, string> = {
  anchors: "Rewrite the 0/1/2 anchors",
  questions: "Rewrite sampled questions",
  replace_belief: "Replace the belief",
};

const text = (size: string, color = "var(--foreground)") => ({ fontSize: size, color });

// ── Word diff ──────────────────────────────────────────────────────────────

type Piece = { kind: "same" | "add" | "del"; text: string };

/** Word-level diff (LCS over whitespace-separated tokens). Anchors and
 *  questions are a sentence or two, so the quadratic table is fine. */
export function diffWords(before: string, after: string): Piece[] {
  const a = before.split(/(\s+)/).filter((t) => t !== "");
  const b = after.split(/(\s+)/).filter((t) => t !== "");
  const n = a.length;
  const m = b.length;
  const table: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      table[i][j] = a[i] === b[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  const out: Piece[] = [];
  const push = (kind: Piece["kind"], t: string) => {
    const last = out[out.length - 1];
    if (last && last.kind === kind) last.text += t;
    else out.push({ kind, text: t });
  };
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { push("same", a[i]); i++; j++; }
    else if (table[i + 1][j] >= table[i][j + 1]) { push("del", a[i]); i++; }
    else { push("add", b[j]); j++; }
  }
  while (i < n) push("del", a[i++]);
  while (j < m) push("add", b[j++]);
  return out;
}

function WordDiff({ before, after }: { before: string; after: string }) {
  const pieces = useMemo(() => diffWords(before || "", after || ""), [before, after]);
  return (
    <span style={{ ...text("var(--text-xs)"), lineHeight: 1.6 }}>
      {pieces.map((p, i) =>
        p.kind === "same" ? (
          <span key={i}>{p.text}</span>
        ) : p.kind === "add" ? (
          <ins key={i} style={{ backgroundColor: "rgba(16,185,129,0.18)", textDecoration: "none" }}>{p.text}</ins>
        ) : (
          <del key={i} style={{ backgroundColor: "rgba(220,38,38,0.14)", color: "var(--muted-foreground)" }}>{p.text}</del>
        ),
      )}
    </span>
  );
}

// ── The proposal ───────────────────────────────────────────────────────────

function AnchorDiff({ before, after }: { before: CalibrationAnchors; after: CalibrationAnchors }) {
  return (
    <div className="space-y-1.5">
      {SCORE_FIELDS.map((field) => (
        <div key={field} className="flex gap-2">
          <span style={{ ...text("var(--text-xs)", "var(--muted-foreground)"), fontWeight: 700, minWidth: 16 }}>
            {SCORE_LABEL[field]}
          </span>
          <WordDiff before={before[field] ?? ""} after={after[field] ?? ""} />
        </div>
      ))}
      {after.examples && (
        <div style={text("var(--text-xs)", "var(--muted-foreground)")}>
          Example answers: {(["2", "1", "0"] as const).map((level) => (
            <div key={level} className="flex gap-2 mt-0.5">
              <span style={{ fontWeight: 700, minWidth: 16 }}>{level}</span>
              <WordDiff before={before.examples?.[level] ?? ""} after={after.examples?.[level] ?? ""} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QuestionDiff({ changes }: { changes: CalibrationQuestionChange[] }) {
  return (
    <div className="space-y-2">
      {changes.map((c) => {
        const added = c.after.variable_ids.filter((v) => !c.before.variable_ids.includes(v));
        const removed = c.before.variable_ids.filter((v) => !c.after.variable_ids.includes(v));
        return (
          <div key={c.id}>
            <div style={{ ...text("var(--text-xs)", "var(--muted-foreground)"), fontWeight: 600 }}>{c.id}</div>
            <WordDiff before={c.before.text} after={c.after.text} />
            {(added.length > 0 || removed.length > 0) && (
              <div style={text("10px", "var(--muted-foreground)")}>
                Beliefs: {added.map((v) => `+${v}`).concat(removed.map((v) => `−${v}`)).join(" ")}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** What the proposal would apply: the admin's edit when there is one. */
function appliedContent(p: CalibrationProposal) {
  if (p.decision?.edited && p.decision.content) return p.decision.content;
  return { anchors: p.anchors, questions: p.questions };
}

function ProposalEditor({
  proposal, onCancel, onSave, busy,
}: {
  proposal: CalibrationProposal;
  onCancel: () => void;
  onSave: (edited: NonNullable<DecideArgs["edited"]>) => void;
  busy: boolean;
}) {
  const [anchors, setAnchors] = useState<CalibrationAnchors | undefined>(
    proposal.anchors ? JSON.parse(JSON.stringify(proposal.anchors)) : undefined,
  );
  const [questions, setQuestions] = useState(
    (proposal.questions ?? []).map((q) => ({ id: q.id, text: q.after.text, variable_ids: q.after.variable_ids })),
  );
  const area = {
    width: "100%", fontSize: "var(--text-xs)", padding: "6px 8px", borderRadius: 6,
    border: "1px solid var(--border)", backgroundColor: "var(--card)", color: "var(--foreground)",
  } as const;
  return (
    <div className="space-y-2">
      {anchors && SCORE_FIELDS.map((field) => (
        <label key={field} className="block">
          <span style={text("10px", "var(--muted-foreground)")}>Score {SCORE_LABEL[field]}</span>
          <textarea
            rows={2} style={area} value={anchors[field]}
            onChange={(e) => setAnchors({ ...anchors, [field]: e.target.value })}
          />
        </label>
      ))}
      {anchors?.examples && (["2", "1", "0"] as const).map((level) => (
        <label key={level} className="block">
          <span style={text("10px", "var(--muted-foreground)")}>Example answer, score {level}</span>
          <textarea
            rows={2} style={area} value={anchors.examples?.[level] ?? ""}
            onChange={(e) => setAnchors({
              ...anchors, examples: { ...(anchors.examples as Record<"0" | "1" | "2", string>), [level]: e.target.value },
            })}
          />
        </label>
      ))}
      {questions.map((q, i) => (
        <label key={q.id} className="block">
          <span style={text("10px", "var(--muted-foreground)")}>{q.id} — beliefs {q.variable_ids.join(", ")}</span>
          <textarea
            rows={2} style={area} value={q.text}
            onChange={(e) => setQuestions(questions.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
          />
        </label>
      ))}
      <div className="flex gap-2">
        <SmallButton
          onClick={() => onSave(anchors ? { anchors } : { questions })}
          busy={busy} icon={<Check className="w-3.5 h-3.5" />} label="Accept edited" tone="go"
        />
        <SmallButton onClick={onCancel} icon={<X className="w-3.5 h-3.5" />} label="Cancel" />
      </div>
    </div>
  );
}

// ── One flagged belief ─────────────────────────────────────────────────────

export interface DecideArgs {
  proposalId: string;
  action: "accept" | "reject";
  edited?: { anchors?: CalibrationAnchors; questions?: { id: string; text: string; variable_ids: string[] }[] };
  note?: string;
}

export function DiagnosisCard({
  belief, round, maxRounds, canDecide, decisionInFlight, applyBlocked, onDecide,
}: {
  belief: CalibrationBeliefDiagnosis;
  round: number;
  maxRounds: number;
  canDecide: boolean;
  /** A decision or apply is in flight on this calibration, on any card. */
  decisionInFlight: boolean;
  /** The framework fails its approval checks already, so a rewrite cannot be
   *  applied. Replacing a belief goes back to Stage 3, and stays open. */
  applyBlocked: boolean;
  onDecide: (args: DecideArgs) => Promise<void>;
}) {
  const [showAnswers, setShowAnswers] = useState(false);
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<null | "accept" | "reject">(null);
  const proposal = belief.proposal;
  const lastRound = round >= maxRounds;
  const isRewrite = !!proposal && proposal.kind !== "replace_belief";
  const roundBlocked = isRewrite && lastRound;
  const acceptBlocked = roundBlocked || (isRewrite && applyBlocked);
  const waiting = !!busy || decisionInFlight;

  const decide = async (action: "accept" | "reject", edited?: DecideArgs["edited"]) => {
    if (!proposal) return;
    setBusy(action);
    try {
      await onDecide({ proposalId: proposal.id, action, edited, note: note.trim() || undefined });
      setEditing(false);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-lg p-3 space-y-2" style={{ border: "1px solid var(--border)", backgroundColor: "var(--card)" }}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <span style={{ ...text("var(--text-xs)"), fontWeight: 700 }}>{belief.variable_id}</span>
          <span style={{ ...text("var(--text-xs)", "var(--muted-foreground)"), marginLeft: 6 }}>{belief.claim}</span>
        </div>
        <span className="flex gap-1">
          {belief.flags.map((f) => (
            <span key={f} className="px-1.5 py-0.5 rounded" style={{
              backgroundColor: "rgba(220,38,38,0.10)", color: "var(--infrared-coral)", fontSize: "10px", fontWeight: 700,
            }}>{FLAG_LABEL[f] ?? f}</span>
          ))}
        </span>
      </div>

      {belief.status === "failed" ? (
        <div style={text("var(--text-xs)", "var(--infrared-coral)")}>
          No diagnosis: {belief.error}
        </div>
      ) : belief.status === "skipped" ? (
        <div style={text("var(--text-xs)", "var(--muted-foreground)")}>{belief.reason}</div>
      ) : (
        <>
          <div>
            <div style={{ ...text("var(--text-xs)"), fontWeight: 600 }}>{belief.cause_label}</div>
            <p style={{ ...text("var(--text-xs)"), margin: "2px 0 0" }}>{belief.diagnosis}</p>
            {(belief.quotes ?? []).map((q, i) => (
              <blockquote key={i} style={{
                ...text("var(--text-xs)", "var(--muted-foreground)"), margin: "4px 0 0", paddingLeft: 8,
                borderLeft: "2px solid var(--chart-3)",
              }}>
                <strong>{q.ref}</strong> “{q.quote}”
              </blockquote>
            ))}
          </div>

          <div>
            <div style={{ ...text("10px", "var(--muted-foreground)"), fontWeight: 700, textTransform: "uppercase" }}>
              Current anchors
            </div>
            {SCORE_FIELDS.map((field) => (
              <div key={field} className="flex gap-2" style={text("var(--text-xs)")}>
                <span style={{ fontWeight: 700, minWidth: 16, color: "var(--muted-foreground)" }}>{SCORE_LABEL[field]}</span>
                <span>{belief.anchors[field]}</span>
              </div>
            ))}
          </div>

          <button
            onClick={() => setShowAnswers(!showAnswers)}
            className="flex items-center gap-1"
            style={{ ...text("var(--text-xs)", "var(--alpha-teal)"), background: "none", border: "none", padding: 0, cursor: "pointer", fontWeight: 600 }}
          >
            {showAnswers ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            {belief.answers.length} sampled answer{belief.answers.length === 1 ? "" : "s"} with the judge's evidence
          </button>
          {showAnswers && (
            <div className="space-y-2">
              {belief.answers.map((a) => (
                <div key={a.ref} className="rounded p-2" style={{ backgroundColor: "var(--muted)" }}>
                  <div style={{ ...text("10px", "var(--muted-foreground)"), fontWeight: 700 }}>
                    {a.ref} · {a.query_id} · {a.model ?? "?"} · {a.applicable ? `score ${a.score}` : "not written to surface it"}
                  </div>
                  {a.judge_evidence && (
                    <div style={text("10px", "var(--muted-foreground)")}>Judge: {a.judge_evidence}</div>
                  )}
                  <div style={{ ...text("var(--text-xs)"), whiteSpace: "pre-wrap" }}>
                    {a.text}{a.truncated ? " …" : ""}
                  </div>
                </div>
              ))}
              {belief.evidence.length > 0 && (
                <div style={text("10px", "var(--muted-foreground)")}>
                  Brand evidence: {belief.evidence.map((e) => `${e.ref} “${e.quote}”`).join(" · ")}
                </div>
              )}
            </div>
          )}

          {proposal && (
            <div className="rounded p-2 space-y-2" style={{ border: "1px dashed var(--alpha-teal)" }}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span style={{ ...text("var(--text-xs)"), fontWeight: 700 }}>
                  Proposed: {KIND_LABEL[proposal.kind]}
                </span>
                <ProposalStatus proposal={proposal} />
              </div>
              {proposal.rationale && <p style={{ ...text("var(--text-xs)", "var(--muted-foreground)"), margin: 0 }}>{proposal.rationale}</p>}

              {editing ? (
                <ProposalEditor
                  proposal={proposal} busy={busy === "accept" || decisionInFlight}
                  onCancel={() => setEditing(false)}
                  onSave={(edited) => void decide("accept", edited)}
                />
              ) : (
                <>
                  {proposal.kind === "anchors" && proposal.before && (
                    <AnchorDiff before={proposal.before} after={appliedContent(proposal).anchors ?? proposal.before} />
                  )}
                  {proposal.kind === "questions" && (
                    <QuestionDiff changes={appliedContent(proposal).questions ?? []} />
                  )}
                  {proposal.kind === "replace_belief" && (
                    <p style={{ ...text("var(--text-xs)"), margin: 0 }}>
                      {proposal.note} Accepting unlocks the framework and stops the rounds: replace the
                      belief at Stage 2 (regenerate beliefs) or Stage 3 (edit it by hand), approve, and
                      run a new Smoke calibration.
                    </p>
                  )}
                  {proposal.grounding.length > 0 && (
                    <div style={text("10px", "var(--muted-foreground)")}>
                      Built from: {proposal.grounding.map((g) => `${g.ref} “${g.quote}”`).join(" · ")}
                    </div>
                  )}
                </>
              )}

              {canDecide && proposal.status === "pending" && !editing && (
                <div className="space-y-2">
                  <input
                    value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)"
                    style={{
                      width: "100%", fontSize: "var(--text-xs)", padding: "4px 8px", borderRadius: 6,
                      border: "1px solid var(--border)", backgroundColor: "var(--card)", color: "var(--foreground)",
                    }}
                  />
                  <div className="flex gap-2 flex-wrap items-center">
                    <SmallButton
                      onClick={() => void decide("accept")} busy={busy === "accept"} disabled={acceptBlocked || waiting}
                      icon={<Check className="w-3.5 h-3.5" />} label="Accept" tone="go"
                    />
                    {proposal.kind !== "replace_belief" && (
                      <SmallButton
                        onClick={() => setEditing(true)} disabled={acceptBlocked || waiting}
                        icon={<Pencil className="w-3.5 h-3.5" />} label="Edit"
                      />
                    )}
                    <SmallButton
                      onClick={() => void decide("reject")} busy={busy === "reject"} disabled={waiting}
                      icon={<X className="w-3.5 h-3.5" />} label="Reject"
                    />
                    {roundBlocked && (
                      <span style={text("10px", "var(--muted-foreground)")}>
                        Round {round} of {maxRounds}: no round is left to test a change. Reject and
                        acknowledge with a reason, or unlock and edit by hand.
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ProposalStatus({ proposal }: { proposal: CalibrationProposal }) {
  if (proposal.status === "pending") {
    return <span style={text("10px", "var(--muted-foreground)")}>awaiting a decision</span>;
  }
  const accepted = proposal.status === "accepted";
  return (
    <span style={{ ...text("10px", accepted ? "var(--chart-3)" : "var(--muted-foreground)"), fontWeight: 700 }}>
      {accepted ? (proposal.decision?.edited ? "accepted (edited)" : "accepted") : "rejected"}
      {proposal.decision?.by ? ` by ${proposal.decision.by}` : ""}
    </span>
  );
}

function SmallButton({
  onClick, busy, disabled, icon, label, tone,
}: {
  onClick: () => void; busy?: boolean; disabled?: boolean; icon: React.ReactNode; label: string; tone?: "go";
}) {
  const off = disabled || busy;
  return (
    <button
      onClick={onClick} disabled={off}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-md"
      style={{
        backgroundColor: tone === "go" ? "var(--alpha-teal)" : "transparent",
        color: tone === "go" ? "white" : "var(--foreground)",
        border: tone === "go" ? "none" : "1px solid var(--border)",
        fontSize: "var(--text-xs)", fontWeight: 600, cursor: off ? "not-allowed" : "pointer", opacity: off ? 0.6 : 1,
      }}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : icon}
      {label}
    </button>
  );
}

// ── Acknowledge with a reason ──────────────────────────────────────────────

export function AcknowledgeForm({ onAcknowledge }: { onAcknowledge: (reason: string) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md"
        style={{
          backgroundColor: "transparent", color: "var(--chart-3)", border: "1px solid var(--chart-3)",
          fontSize: "var(--text-xs)", fontWeight: 600, cursor: "pointer",
        }}
      >
        <ShieldAlert className="w-3.5 h-3.5" />
        Acknowledge flags…
      </button>
    );
  }
  return (
    <div className="w-full space-y-2">
      <textarea
        rows={2} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus
        placeholder="Why these flags are acceptable for this read (recorded on the framework's calibration history)"
        style={{
          width: "100%", fontSize: "var(--text-xs)", padding: "6px 8px", borderRadius: 6,
          border: "1px solid var(--border)", backgroundColor: "var(--card)", color: "var(--foreground)",
        }}
      />
      <div className="flex gap-2">
        <SmallButton
          onClick={async () => {
            setBusy(true);
            try { await onAcknowledge(reason.trim()); setOpen(false); } finally { setBusy(false); }
          }}
          busy={busy} disabled={!reason.trim()} icon={<ShieldAlert className="w-3.5 h-3.5" />}
          label="Acknowledge with this reason" tone="go"
        />
        <SmallButton onClick={() => setOpen(false)} icon={<X className="w-3.5 h-3.5" />} label="Cancel" />
      </div>
    </div>
  );
}

// ── The framework's calibration history ────────────────────────────────────

export function CalibrationHistoryView({ history }: { history: CalibrationHistory }) {
  const [open, setOpen] = useState(false);
  if (!history.calibrations.length) return null;
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5"
        style={{ ...text("var(--text-xs)", "var(--alpha-teal)"), background: "none", border: "none", padding: 0, cursor: "pointer", fontWeight: 600 }}
      >
        <History className="w-3.5 h-3.5" />
        Calibration history ({history.calibrations.length})
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="space-y-3 mt-2">
          {history.calibrations.map((cal) => (
            <div key={cal.root_run_id} className="rounded-lg p-2" style={{ border: "1px solid var(--border)" }}>
              <div style={{ ...text("10px", "var(--muted-foreground)"), fontWeight: 700 }}>
                Calibration from run {cal.root_run_id} · {cal.rounds.length} round{cal.rounds.length === 1 ? "" : "s"} ·
                ${cal.spent_usd.toFixed(2)} of ${history.spend_cap_usd.toFixed(2)} cap
              </div>
              {cal.rounds.map((r) => (
                <div key={r.run_id} className="mt-1.5" style={text("var(--text-xs)")}>
                  <strong>Round {r.round}</strong> · run {r.run_id} · {r.state ?? r.status}
                  {r.flags.length > 0 && ` · ${r.flags.map((f) => `${f.variable_id} ${FLAG_LABEL[f.flag] ?? f.flag}`).join(", ")}`}
                  {r.empty_responses > 0 && ` · ${r.empty_responses} empty`}
                  {` · rubric ${r.rubric_version ?? "?"} / battery ${(r.battery_hash ?? "?").slice(0, 12)}`}
                  {` · $${r.cost_usd.toFixed(2)}`}
                  {r.proposals.length > 0 && (
                    <div style={text("10px", "var(--muted-foreground)")}>
                      {r.proposals.map((p) => `${p.variable_id}: ${KIND_LABEL[p.kind]} — ${p.status}${p.edited ? " (edited)" : ""}${p.decided_by ? ` by ${p.decided_by}` : ""}${p.note ? ` (“${p.note}”)` : ""}`).join(" · ")}
                    </div>
                  )}
                  {r.applied && (
                    <div style={text("10px", "var(--muted-foreground)")}>
                      Applied by {r.applied.by} — {r.applied.mode === "rerun"
                        ? (r.applied.next_run_id ? `re-run as run ${r.applied.next_run_id}`
                          : r.applied.rerun_off ? "automatic re-run off, not started"
                          : `re-run did not start: ${r.applied.rerun_error ?? ""}`)
                        : "framework unlocked to replace a belief"}
                    </div>
                  )}
                  {r.acknowledgement && (
                    <div style={text("10px", "var(--chart-3)")}>
                      Acknowledged by {r.acknowledgement.by}: “{r.acknowledgement.reason}”
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
